import { translate } from "@/i18n";
import { desktopScreenRecordingMessages } from "@/i18n/messages/desktop-screen-recording";
import { invoke } from "@tauri-apps/api/core";

import { uploadImage } from "@/api/uploads";
import type { ImageAttachment } from "@/components/panels/image-preview-strip";
import type { ScreenshotScreenMeta } from "@/lib/pointing-transform";
import type { ScreenRecordingsContext } from "@/lib/screen-recordings-context";
import { isTauri } from "@/lib/is-tauri";
import { subscribeDesktopChatAuthScope } from "./desktop-chat-auth-scope";
import {
  clearScreenRecordingMedia,
  uploadScreenRecordingMedia,
} from "./screen-recording-media";
import {
  ingestScreenRecording,
  type ScreenRecordingClip,
  type ScreenRecordingEvent,
  useScreenRecordingStore,
} from "./screen-recording-store";

const MAX_ATTACHMENTS = 8;
const frameUploads = new Map<
  string,
  Promise<Awaited<ReturnType<typeof uploadImage>>>
>();
export function clearScreenRecordingUploadCache(): void {
  frameUploads.clear();
  clearScreenRecordingMedia();
}
subscribeDesktopChatAuthScope(clearScreenRecordingUploadCache);

export type PreparedScreenRecordings = {
  context?: ScreenRecordingsContext;
  images: ImageAttachment[];
  screens: ScreenshotScreenMeta[];
};

/** Give each clip one sample before spending remaining slots on motion detail. */
export function selectRecordingFrames(
  clips: ScreenRecordingClip[],
  budget: number,
) {
  const counts = clips.map(() => 0);
  let remaining = Math.max(0, Math.floor(budget));
  while (remaining > 0) {
    let assigned = false;
    for (let index = 0; index < clips.length && remaining > 0; index += 1) {
      if (counts[index]! >= Math.min(6, clips[index]!.frames?.length ?? 0))
        continue;
      counts[index]! += 1;
      remaining -= 1;
      assigned = true;
    }
    if (!assigned) break;
  }
  return clips.flatMap((clip, clipIndex) => {
    const frames = clip.frames ?? [];
    const count = counts[clipIndex]!;
    return Array.from({ length: count }, (_, index) => ({
      clip,
      frame:
        frames[
          count === 1
            ? Math.floor((frames.length - 1) / 2)
            : Math.round((index * (frames.length - 1)) / (count - 1))
        ]!,
    }));
  });
}

function sampleFile(
  clipId: string,
  frame: { tMs: number; dataUrl: string },
): File {
  const match =
    /^data:(image\/(?:jpeg|png));base64,([A-Za-z0-9+/=\r\n]+)$/.exec(
      frame.dataUrl,
    );
  if (!match) throw new Error("Recording sample is not a supported image");
  const decoded = atob(match[2]!);
  const bytes = Uint8Array.from(decoded, (char) => char.charCodeAt(0));
  return new File(
    [bytes],
    `recording-${clipId}-frame-${Math.round(frame.tMs)}.${match[1] === "image/png" ? "png" : "jpg"}`,
    { type: match[1] },
  );
}

/** Native finalization barrier without requiring every clip to have succeeded. */
export async function collectScreenRecordings(
  sessionIds: string[],
): Promise<ScreenRecordingClip[]> {
  const selected = new Set(sessionIds);
  if (sessionIds.length === 0) return [];
  if (isTauri()) {
    const ready = await invoke<ScreenRecordingEvent[]>(
      "finish_screen_recordings",
      { attentionSessionIds: sessionIds },
    );
    // The command's return is the barrier; event delivery may still be queued.
    if (Array.isArray(ready))
      for (const clip of ready) ingestScreenRecording(clip);
  }
  const clips = Object.values(useScreenRecordingStore.getState().clips).filter(
    (clip) => selected.has(clip.attentionSessionId),
  );
  return clips;
}

export async function finishScreenRecordings(
  sessionIds: string[],
): Promise<ScreenRecordingClip[]> {
  const clips = await collectScreenRecordings(sessionIds);
  if (clips.length > 8)
    throw new Error(
      translate(desktopScreenRecordingMessages, "tooManyClips", { max: 8 }),
    );
  const incomplete = clips.find((clip) => clip.phase !== "ready" || !clip.path);
  if (incomplete)
    throw new Error(
      incomplete.error ||
        translate(desktopScreenRecordingMessages, "stillSaving"),
    );
  return clips;
}

/** Reuse image IDs on retries so the request receipt remains idempotent. */
function uploadRecordingFrame(
  clip: ScreenRecordingClip,
  frame: { tMs: number; dataUrl: string },
) {
  const key = `${clip.attentionSessionId}/${clip.clipId}/${frame.tMs}`;
  let uploaded = frameUploads.get(key);
  if (!uploaded) {
    uploaded = uploadImage(sampleFile(clip.clipId, frame)).catch((error) => {
      frameUploads.delete(key);
      throw error;
    });
    frameUploads.set(key, uploaded);
    if (frameUploads.size > 256)
      frameUploads.delete(frameUploads.keys().next().value!);
  }
  return uploaded;
}

/** Keep bounded samples for model context and attach originals when slots fit. */
export async function prepareScreenRecordings(
  sessionIds: string[],
  attachmentCount: number,
  isCancelled: () => boolean = () => false,
  supportsImages = true,
): Promise<PreparedScreenRecordings> {
  const clips = await finishScreenRecordings(sessionIds);
  const cancelled = () =>
    isCancelled() ||
    clips.some(
      (clip) => !useScreenRecordingStore.getState().clips[clip.clipId],
    );
  if (cancelled() || clips.length === 0) return { images: [], screens: [] };
  const available = Math.max(0, MAX_ATTACHMENTS - attachmentCount);
  const sampleReserve = supportsImages
    ? Math.min(available, clips.filter((clip) => clip.frames?.length).length)
    : 0;
  const originals = isTauri() ? clips.slice(0, available - sampleReserve) : [];
  const videoUploads = await Promise.all(
    originals.map(async (clip) => ({
      clip,
      uploaded: await uploadScreenRecordingMedia(clip.clipId),
    })),
  );
  if (cancelled()) return { images: [], screens: [] };
  const videos: ImageAttachment[] = videoUploads.flatMap(
    ({ clip, uploaded }) =>
      uploaded
        ? [
            {
              id: `recording-video-${clip.clipId}`,
              imageId: uploaded.id,
              url: uploaded.url,
              filename:
                uploaded.original_filename || `recording-${clip.clipId}.mp4`,
              contentType: "video/mp4",
            },
          ]
        : [],
  );
  const selected = selectRecordingFrames(
    clips,
    supportsImages ? available - videos.length : 0,
  );
  const uploads = await Promise.all(
    selected.map(async ({ clip, frame }) => {
      if (cancelled()) return null;
      const image = await uploadRecordingFrame(clip, frame);
      return {
        clip,
        frame,
        image,
        filename: `recording-${clip.clipId}-frame-${Math.round(frame.tMs)}.${frame.dataUrl.startsWith("data:image/png") ? "png" : "jpg"}`,
      };
    }),
  );
  if (cancelled()) return { images: [], screens: [] };
  const images: ImageAttachment[] = [];
  const screens: ScreenshotScreenMeta[] = [];
  const videoIds = new Map(
    videos.map((video) => [
      video.id.replace("recording-video-", ""),
      video.imageId,
    ]),
  );
  const context: ScreenRecordingsContext = {
    version: 1,
    clips: clips.map((clip) => ({
      id: clip.clipId,
      attention_session_id: clip.attentionSessionId,
      original_path: clip.path!,
      ...(videoIds.get(clip.clipId)
        ? { video_id: videoIds.get(clip.clipId) }
        : {}),
      started_ms: clip.startedMs,
      duration_ms: clip.durationMs ?? 0,
      samples: [],
    })),
  };
  for (const upload of uploads) {
    if (!upload) continue;
    const { clip, frame, image, filename } = upload;
    context.clips
      .find((item) => item.id === clip.clipId)!
      .samples.push({ image_id: image.id, t_ms: frame.tMs });
    if (clip.screen)
      screens.push({
        ...clip.screen,
        index: attachmentCount + images.length,
        scaledWidth: image.width,
        scaledHeight: image.height,
      });
    images.push({
      id: `recording-${clip.clipId}-${frame.tMs}`,
      imageId: image.id,
      url: image.url,
      width: image.width,
      height: image.height,
      filename,
    });
  }
  return { context, images: [...images, ...videos], screens };
}
